'use client';

import { useState, useEffect } from 'react';

interface Country {
  id: string;
  name: string;
  emoji: string;
  region: string;
}

export default function GlobalPresenceSection() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const countries: Country[] = [
    { id: 'country_germany', name: 'Germany', emoji: '🇩🇪', region: 'Europe' },
    { id: 'country_poland', name: 'Poland', emoji: '🇵🇱', region: 'Europe' },
    { id: 'country_switzerland', name: 'Switzerland', emoji: '🇨🇭', region: 'Europe' },
    { id: 'country_spain', name: 'Spain', emoji: '🇪🇸', region: 'Europe' },
    { id: 'country_uk', name: 'United Kingdom', emoji: '🇬🇧', region: 'Europe' },
    { id: 'country_estonia', name: 'Estonia', emoji: '🇪🇪', region: 'Europe' },
    { id: 'country_greece', name: 'Greece', emoji: '🇬🇷', region: 'Europe' },
    { id: 'country_uae', name: 'UAE', emoji: '🇦🇪', region: 'Middle East' },
    { id: 'country_thailand', name: 'Thailand', emoji: '🇹🇭', region: 'Asia' },
    { id: 'country_taiwan', name: 'Taiwan', emoji: '🇹🇼', region: 'Asia' },
    { id: 'country_philippines', name: 'Philippines', emoji: '🇵🇭', region: 'Asia' },
    { id: 'country_usa', name: 'USA', emoji: '🇺🇸', region: 'Americas' },
  ];

  const stats = [
    { value: '12', label: 'Countries' },
    { value: '4', label: 'Continents' },
    { value: '24/7', label: 'Available' },
  ];

  return (
    <section className="py-24 pb-12 px-6 md:px-12 bg-muted relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #2D2D2D 1px, transparent 1px), radial-gradient(circle at 80% 20%, #2D2D2D 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <span className="text-xs font-bold text-secondary uppercase tracking-widest mb-3 block">
              International Presence
            </span>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight leading-tight text-foreground">
              Globally connected,<br />
              <span className="text-muted-foreground">locally rooted</span>
            </h2>
          </div>
          <p className="text-muted-foreground max-w-sm text-sm leading-relaxed md:text-right">
            We support our clients in 12 countries across 4 continents – with local expertise and international coordination.
          </p>
        </div>

        {/* Stats row */}
        <div className="flex gap-8 mb-12 border-b border-border pb-10">
          {stats.map((stat) => (
            <div key={stat.label} className="flex flex-col">
              <span className="text-3xl font-serif text-secondary">{stat.value}</span>
              <span className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Country grid */}
        {isHydrated && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {countries.map((country) => (
              <div
                key={country.id}
                className="group flex flex-col items-center gap-2 bg-card hover:bg-white border border-border hover:border-secondary/50 rounded-xl px-4 py-5 transition-all duration-200 cursor-default shadow-sm hover:shadow-md"
              >
                <span className="text-3xl leading-none">{country.emoji}</span>
                <span className="text-sm font-medium text-foreground text-center leading-tight">{country.name}</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{country.region}</span>
              </div>
            ))}
          </div>
        )}

        {/* Spain Office Address */}
        <div className="mt-8 bg-card border border-secondary/30 rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-3xl">🇪🇸</span>
            <div>
              <span className="text-xs font-bold text-secondary uppercase tracking-widest block">Spain Office</span>
              <span className="text-sm font-semibold text-foreground">cyfire Rechtsanwaltsgesellschaft mbH</span>
            </div>
          </div>
          <div className="sm:border-l sm:border-border sm:pl-6 text-sm text-muted-foreground leading-relaxed">
            Avda. de Europa 26 ATICA 5, 2ª planta · 28224 Pozuelo de Alarcón · Community of Madrid
          </div>
        </div>

        {/* Germany Office Address */}
        <div className="mt-4 bg-card border border-secondary/30 rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-3xl">🇩🇪</span>
            <div>
              <span className="text-xs font-bold text-secondary uppercase tracking-widest block">Germany Office</span>
              <span className="text-sm font-semibold text-foreground">cyfire Rechtsanwaltsgesellschaft mbH</span>
            </div>
          </div>
          <div className="sm:border-l sm:border-border sm:pl-6 text-sm text-muted-foreground leading-relaxed">
            Thurn-Und-Taxis-Platz 6 · 60313 Frankfurt · Hessen
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 pt-10 border-t border-border">
          <p className="text-muted-foreground text-sm">
            Your contact for international mandates:
          </p>
          <a
            href="mailto:international@cyfire.net"
            className="text-sm font-medium text-secondary hover:text-secondary/80 transition-colors border border-secondary/30 hover:border-secondary/60 rounded-full px-5 py-2"
          >
            international@cyfire.net →
          </a>
        </div>
      </div>
    </section>
  );
}
